import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Alert,
} from '@mui/material';
import { ArrowForward, ArrowBack, Edit, Undo } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { paymentScheduleFieldStyles } from './PaymentScheduleField.styles';

const MONTHS = [
  { key: 'january', label: 'Ocak', value: 1 },
  { key: 'february', label: 'Şubat', value: 2 },
  { key: 'march', label: 'Mart', value: 3 },
  { key: 'april', label: 'Nisan', value: 4 },
  { key: 'may', label: 'Mayıs', value: 5 },
  { key: 'june', label: 'Haziran', value: 6 },
  { key: 'july', label: 'Temmuz', value: 7 },
  { key: 'august', label: 'Ağustos', value: 8 },
  { key: 'september', label: 'Eylül', value: 9 },
  { key: 'october', label: 'Ekim', value: 10 },
  { key: 'november', label: 'Kasım', value: 11 },
  { key: 'december', label: 'Aralık', value: 12 },
];

const PaymentScheduleField = ({
  value = [],
  onChange,
  label,
  error,
  helperText,
  formData = {},
  onValidate,
  field,
  entityId,
}) => {

  const firmsList = field.firmsList || [];
  const jobsList = field.jobsList || [];
  const pricingProcessList = field.pricingProcessList || [];
  const { t } = useTranslation();
  const [transferringFrom, setTransferringFrom] = useState(null);
  const [isEditingAmounts, setIsEditingAmounts] = useState(false);
  
  // Determine if we're in edit mode (entityId exists) or create mode (entityId is null)
  const isEditMode = !!entityId;
  
  // Get parent item amount if this is inside an array field (invoice code split)
  const parentAmount = field.parentItem?.amount;
  
  // Get total unit price from form data (for normal invoice case)
  const totalUnitPrice = formData?.total_unit_price;
  
  // Get custom date range settings
  const customDateRange = formData?.custom_date_range === true || formData?.custom_date_range === 'true' || formData?.custom_date_range === 1;
  const dateRangeStart = formData?.date_range_start;
  const dateRangeEnd = formData?.date_range_end;
  
  const getDateFromString = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString);
  };

  const addMonths = (date, months) => {
    const result = new Date(date);
    result.setMonth(result.getMonth() + months);
    return result;
  };

  const getMonthRange = (startDate, endDate) => {
    if (!startDate || !endDate) return [];
    
    const months = [];
    const current = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
    
    while (current <= end) {
      const monthIndex = current.getMonth();
      const month = MONTHS[monthIndex];
      const year = current.getFullYear();
      months.push({
        month: month.value,
        monthName: month.label,
        monthKey: month.key,
        year: year,
        yearMonth: `${year} ${month.label}`, 
        is_active: true,
        amount: 0,
      });
      current.setMonth(current.getMonth() + 1);
    }
    
    return months;
  };

  const getCustomMonthRange = () => {
    if (!customDateRange || !dateRangeStart || !dateRangeEnd) return [];
    
    const startDate = getDateFromString(dateRangeStart);
    const endDate = getDateFromString(dateRangeEnd);
    
    if (!startDate || !endDate) return [];
    
    return getMonthRange(startDate, endDate);
  };

  const getNextMonth = (date) => {
    return addMonths(date, 1);
  };

  const getContractData = (contractId) => {
    if (!contractId) return null;
    
    const selectedFirmId = formData.firm_established === 'yes' 
      ? formData.firmId 
      : formData.firm_short_name;
    
    if (!selectedFirmId) return null;
    
    const selectedFirm = firmsList.find(f => f.id === parseInt(selectedFirmId) || f.id === selectedFirmId);
    
    if (!selectedFirm || !selectedFirm.contracts) return null;
    
    return selectedFirm.contracts.find(c => c.id === parseInt(contractId) || c.id === contractId);
  };

  const getJobData = (jobId) => {
    if (!jobId) return null;
    
    return jobsList.find(j => j.id === jobId);
  };

  const getPricingProcessData = (pricingProcessId) => {
    if (!pricingProcessId) return null;
    
    return pricingProcessList.find(p => p.id === parseInt(pricingProcessId) || p.id === pricingProcessId);
  };

  const calculatePaymentSchedule = () => {
    const contractId = formData.contractId;
    const pricingProcessId = formData.pricingProcessId;
    const jobId = formData.jobId;
    
    if (!contractId || !pricingProcessId) {
      return { schedules: [], warning: null };
    }
    
    const contract = getContractData(contractId);
    const job = getJobData(jobId);
    const pricingProcess = getPricingProcessData(pricingProcessId);
    
    if (!contract || !pricingProcess) {
      return { schedules: [], warning: null };
    }

    const signingDate = getDateFromString(contract.contract_signing_date);
    const invoiceIssueDate = getDateFromString(contract.invoice_issue_date);
    // Kural: sözleşme imza ve fatura kesilme tarihinden hangisi sonraysa onu baz al
    const contractSigningDate = (signingDate && invoiceIssueDate)
      ? (invoiceIssueDate > signingDate ? invoiceIssueDate : signingDate)
      : (invoiceIssueDate || signingDate);
    let workStartDate = getDateFromString(contract.work_start_date);
    const isProjectBased = contract.contract_subject?.is_project_based;
    const pricingMethodName = contract.pricing_method?.name;
    const hasInflationClause = contract.inflation_clause;
    const renewalPeriodMonths = parseInt(contract.renewal_period) || 12;
    const firstPriceIncreaseDate = getDateFromString(
      contract.first_price_increase_period
    );
    const autoRenewal = contract.auto_renewal === true;
    const referenceDate = formData?.new_start_date
      ? getDateFromString(formData.new_start_date)
      : new Date();

    const getCurrentRenewalRange = (baseDate, periodInMonths, refDate) => {
      if (!baseDate || !periodInMonths || periodInMonths <= 0) {
        const fallbackMonths = renewalPeriodMonths || 12;
        return { start: baseDate, end: baseDate ? addMonths(baseDate, fallbackMonths) : null };
      }
      
      const baseMonth = baseDate.getMonth();
      const refYear = refDate.getFullYear();
      const refMonth = refDate.getMonth();
      
      let startYear = refYear;
      if (refMonth < baseMonth) {
        startYear = refYear - 1;
      }
      
      const periodStart = new Date(startYear, baseMonth, 1);
      
      const periodEnd = addMonths(periodStart, periodInMonths - 1);
      
      return { start: periodStart, end: periodEnd };
    };

    // Yardımcı: ilk ücret artışı dönemi varsa, bitiş tarihini o aya kadar kısıtla
    const getScheduleMonths = (startDate, endDate) => {
      if (!startDate || !endDate) return [];

      let finalEnd = endDate;
      if (firstPriceIncreaseDate) {
        const incMonth = new Date(
          firstPriceIncreaseDate.getFullYear(),
          firstPriceIncreaseDate.getMonth(),
          1
        );
        const startMonth = new Date(
          startDate.getFullYear(),
          startDate.getMonth(),
          1
        );
        // Eğer ilk artış dönemi başlangıçtan sonra ama mevcut endDate'den önceyse, onu kullan
        if (incMonth >= startMonth && incMonth < endDate) {
          finalEnd = incMonth;
        }
      }
      return getMonthRange(startDate, finalEnd);
    };

    // kural: Otomatik yenileme varsa ilk dönem içinde miyiz, yoksa sonraki dönemde mi?
    // baseDate: renewal donemlerinin ne'den itibaren sayilacagi.
    // Default: contractSigningDate (mevcut davranis). Onay Sonrasi + auto
    // renewal durumunda caller `workStartDate`i (projectStartDate override
    // sonrasi) verir — Kahuna/Iber Tekno: proje 2024-07-01 baz, imza 2025-03
    // degil. Boylece renewal donemleri projeye gore hizalanir.
    const getRenewalScheduleRange = (baseDate = contractSigningDate) => {
      if (!baseDate || !renewalPeriodMonths) return null;
      const baseStart = new Date(
        baseDate.getFullYear(),
        baseDate.getMonth(),
        1
      );
      const refMonthStart = new Date(
        referenceDate.getFullYear(),
        referenceDate.getMonth(),
        1
      );
      let firstPeriodEnd;
      if (firstPriceIncreaseDate) {
        const increaseMonthStart = new Date(
          firstPriceIncreaseDate.getFullYear(),
          firstPriceIncreaseDate.getMonth(),
          1
        );
        firstPeriodEnd = addMonths(increaseMonthStart, -1);
      } else {
        firstPeriodEnd = addMonths(baseStart, renewalPeriodMonths - 1);
      }
      if (refMonthStart <= firstPeriodEnd) {
        return { start: baseStart, end: firstPeriodEnd };
      }
      if (firstPriceIncreaseDate && firstPriceIncreaseDate.getMonth() === 0) {
        const refYear = referenceDate.getFullYear();
        return {
          start: new Date(refYear, 0, 1),
          end: new Date(refYear, 11, 1),
        };
      }
      const baseMonth = baseStart.getMonth();
      const refYear = referenceDate.getFullYear();
      const refMonth = referenceDate.getMonth();
      let startYear = refYear;
      if (refMonth < baseMonth) startYear = refYear - 1;
      const periodStart = new Date(startYear, baseMonth, 1);
      const periodEnd = addMonths(periodStart, renewalPeriodMonths - 1);
      return { start: periodStart, end: periodEnd };
    };

    const getDynamicDateRange = (baseDate, monthsToShow = 12) => {
      if (!baseDate) return { start: null, end: null };
      
      const baseMonth = baseDate.getMonth();
      const refYear = referenceDate.getFullYear();
      const refMonth = referenceDate.getMonth();
      
      let startYear = refYear;
      if (refMonth < baseMonth) {
        startYear = refYear - 1;
      }
      
      const periodStart = new Date(startYear, baseMonth, 1);
      const periodEnd = addMonths(periodStart, monthsToShow - 1);
      
      return { start: periodStart, end: periodEnd };
    };

    const processNameLower = pricingProcess?.name?.toLowerCase() || '';
    const isWritingProcess = processNameLower.includes('yazım') || processNameLower.includes('writing');
    const isAfterApprovalProcess = processNameLower.includes('onay') || processNameLower.includes('approval');

    // SPEC (Iber Tekno / Karuhan case'i): Onay sonrasi fatura olustururken
    // odeme takvimi (schedule) proje baslangic tarihinden itibaren
    // olusturulmalidir. Iber Tekno TUBITAK ornegi: projectStartDate 2025-09-01,
    // ama work_start_date/imza tarihine gore sistem Mart'tan basliyordu.
    //
    // "Fatura girildigi andaki proje baslangic tarihi neyse o" — snapshot
    // istegi: fatura formunda o an okunan tarih baz alinir; sonradan proje
    // tarihi degisse bile bu fatura kaydi zaten olusmus olacagi icin dogal
    // olarak etkilenmez (yeni fatura icin yeniden okunur).
    if (isAfterApprovalProcess && Array.isArray(job?.job_details)) {
      for (const d of job.job_details) {
        const s = d?.detail_data?.projectStartDate;
        if (s) { workStartDate = getDateFromString(s); break; }
      }
    }
    const isContinuousPayment = processNameLower.includes('sürekli') || processNameLower.includes('surekli');
    let schedules = [];
    let warning = null;

    if (isContinuousPayment || isAfterApprovalProcess) {
      if (contractSigningDate) {
        // Onay Sonrasi + projectStartDate override edildi → renewal donemlerini
        // ve non-renewal range'i projectStartDate baz al. Sadece surekli odeme
        // durumunda contractSigningDate kalir (mevcut davranis).
        const scheduleBaseDate = (isAfterApprovalProcess && workStartDate)
          ? workStartDate
          : contractSigningDate;
        if (autoRenewal) {
          const range = getRenewalScheduleRange(scheduleBaseDate);
          if (range) {
            schedules = getMonthRange(range.start, range.end);
          }
        } else {
          const months = renewalPeriodMonths || 12;
          const { start, end } = getDynamicDateRange(scheduleBaseDate, months);
          if (start && end) {
            schedules = getScheduleMonths(start, end);
          }
        }
      }
      return { schedules, warning };
    }
    if (isProjectBased) {
      if (pricingMethodName?.includes('Yazım Sabit, Onay Sonrası Aylık Sabit')) {
        if (isWritingProcess) {
          if (contractSigningDate) {
            const { start, end } = getDynamicDateRange(contractSigningDate, 2);
            if (start && end) {
              schedules = getScheduleMonths(start, end);
            }
          }
        } else if (isAfterApprovalProcess) {
          if (hasInflationClause) {
            if (workStartDate && renewalPeriodMonths) {
              const { start, end } = getDynamicDateRange(workStartDate, renewalPeriodMonths);
              if (start && end) {
                schedules = getScheduleMonths(start, end);
              }
            }
          } else {
            if (workStartDate) {
              const months = renewalPeriodMonths || 12;
              const { start, end } = getDynamicDateRange(workStartDate, months);
              if (start && end) {
                schedules = getScheduleMonths(start, end);
              }
            }
          }
        }
      } else if (pricingMethodName?.includes('Yazım Yok, Onay Sonrası ( sadece), Aylık %')) {
        if (isWritingProcess) {
          warning = t('invoices.form.paymentSchedule.warnings.revisePricingMethod');
        } else if (isAfterApprovalProcess) {
          if (workStartDate) {
            const months = renewalPeriodMonths || 12;
            const { start, end } = getDynamicDateRange(workStartDate, months);
            if (start && end) {
              schedules = getScheduleMonths(start, end);
            }
          }
        }
      } else if (pricingMethodName?.includes('Yazım Yok, Onay Sonrası (sadece ) Aylık Sabit')) {
        if (isWritingProcess) {
          warning = t('invoices.form.paymentSchedule.warnings.revisePricingMethod');
        } else if (isAfterApprovalProcess) {
          if (hasInflationClause) {
            if (workStartDate && renewalPeriodMonths) {
              const { start, end } = getDynamicDateRange(workStartDate, renewalPeriodMonths);
              if (start && end) {
                schedules = getMonthRange(start, end);
              }
            }
          } else {
            if (workStartDate) {
              const months = renewalPeriodMonths || 12;
              const { start, end } = getDynamicDateRange(workStartDate, months);
              if (start && end) {
                schedules = getScheduleMonths(start, end);
              }
            }
          }
        }
      } else if (pricingMethodName?.includes('Yazım Ücretsiz, Onay Sonrası Aylık Sabit')) {
        if (isWritingProcess) {
          warning = t('invoices.form.paymentSchedule.warnings.revisePricingMethod');
        } else if (isAfterApprovalProcess) {
          if (hasInflationClause) {
            if (workStartDate && renewalPeriodMonths) {
              const { start, end } = getDynamicDateRange(workStartDate, renewalPeriodMonths);
              if (start && end) {
                schedules = getMonthRange(start, end);
              }
            }
          } else {
            if (workStartDate) {
              const months = renewalPeriodMonths || 12;
              const { start, end } = getDynamicDateRange(workStartDate, months);
              if (start && end) {
                schedules = getScheduleMonths(start, end);
              }
            }
          }
        }
      } else if (pricingMethodName?.includes('Yazım Sabit, Onay sonrası % ile')) {
        if (isWritingProcess) {
          if (contractSigningDate) {
            const { start, end } = getDynamicDateRange(contractSigningDate, 2);
            if (start && end) {
              schedules = getScheduleMonths(start, end);
            }
          }
        } else if (isAfterApprovalProcess) {
          if (workStartDate) {
            const months = renewalPeriodMonths || 12;
            const { start, end } = getDynamicDateRange(workStartDate, months);
            if (start && end) {
              schedules = getScheduleMonths(start, end);
            }
          }
        }
      } else if (pricingMethodName?.includes('Aylık Sabit ( Başvuru ve Onay sonrası )')) {
        if (isWritingProcess) {
          if (contractSigningDate) {
            const { start, end } = getDynamicDateRange(contractSigningDate, 2);
            if (start && end) {
              schedules = getScheduleMonths(start, end);
            }
          }
        } else if (isAfterApprovalProcess) {
          if (hasInflationClause) {
            if (workStartDate && renewalPeriodMonths) {
              const { start, end } = getDynamicDateRange(workStartDate, renewalPeriodMonths);
              if (start && end) {
                schedules = getMonthRange(start, end);
              }
            }
          } else {
            if (workStartDate) {
              const months = renewalPeriodMonths || 12;
              const { start, end } = getDynamicDateRange(workStartDate, months);
              if (start && end) {
                schedules = getMonthRange(start, end);
              }
            }
          }
        }
      } else if (pricingMethodName?.includes('Proje Yazım ( Sadece )')) {
        if (isWritingProcess) {
          if (contractSigningDate) {
            const { start, end } = getDynamicDateRange(contractSigningDate, 2);
            if (start && end) {
              schedules = getMonthRange(start, end);
            }
          }
        } else if (isAfterApprovalProcess) {
          warning = t('invoices.form.paymentSchedule.warnings.changePricingType');
        }
      } else if (pricingMethodName?.includes('Diğer - Tek Ödeme')) {
        if (contractSigningDate) {
          const { start, end } = getDynamicDateRange(contractSigningDate, 2);
          if (start && end) {
            schedules = getScheduleMonths(start, end);
          }
        }
      }
    } else {
      // Sözleşme bazlı mantık
      if (pricingMethodName?.includes('Yazım Sabit, Onay Sonrası Aylık Sabit')) {
        if (isWritingProcess) {
          if (contractSigningDate) {
            const { start, end } = getDynamicDateRange(contractSigningDate, 2);
            if (start && end) {
              schedules = getMonthRange(start, end);
            }
          }
        } else if (isAfterApprovalProcess) {
          if (contractSigningDate && renewalPeriodMonths) {
            const { start, end } = getCurrentRenewalRange(
              contractSigningDate,
              renewalPeriodMonths,
              referenceDate
            );
            if (start && end) {
              schedules = getScheduleMonths(start, end);
            }
          }
        }
      } else if (pricingMethodName?.includes('Yazım Yok, Onay Sonrası ( sadece), Aylık %')) {
        if (isWritingProcess) {
          warning = t('invoices.form.paymentSchedule.warnings.revisePricingMethod');
        } else if (isAfterApprovalProcess) {
          if (contractSigningDate && renewalPeriodMonths) {
            const { start, end } = getCurrentRenewalRange(
              contractSigningDate,
              renewalPeriodMonths,
              referenceDate
            );
            if (start && end) {
              schedules = getScheduleMonths(start, end);
            }
          }
        }
      } else if (pricingMethodName?.includes('Yazım Yok, Onay Sonrası (sadece ) Aylık Sabit')) {
        if (isWritingProcess) {
          warning = t('invoices.form.paymentSchedule.warnings.revisePricingMethod');
        } else if (isAfterApprovalProcess) {
          if (contractSigningDate && renewalPeriodMonths) {
            const { start, end } = getCurrentRenewalRange(
              contractSigningDate,
              renewalPeriodMonths,
              referenceDate
            );
            if (start && end) {
              schedules = getScheduleMonths(start, end);
            }
          }
        }
      } else if (pricingMethodName?.includes('Yazım Ücretsiz, Onay Sonrası Aylık Sabit')) {
        if (isWritingProcess) {
          warning = t('invoices.form.paymentSchedule.warnings.revisePricingMethod');
        } else if (isAfterApprovalProcess) {
          if (contractSigningDate && renewalPeriodMonths) {
            const { start, end } = getCurrentRenewalRange(
              contractSigningDate,
              renewalPeriodMonths,
              referenceDate
            );
            if (start && end) {
              schedules = getScheduleMonths(start, end);
            }
          }
        }
      } else if (pricingMethodName?.includes('Yazım Sabit, Onay sonrası % ile')) {
        if (isWritingProcess) {
          if (contractSigningDate) {
            const { start, end } = getDynamicDateRange(contractSigningDate, 2);
            if (start && end) {
              schedules = getMonthRange(start, end);
            }
          }
        } else if (isAfterApprovalProcess) {
          if (contractSigningDate && renewalPeriodMonths) {
            const { start, end } = getCurrentRenewalRange(
              contractSigningDate,
              renewalPeriodMonths,
              referenceDate
            );
            if (start && end) {
              schedules = getMonthRange(start, end);
            }
          }
        }
      } else if (pricingMethodName?.includes('Aylık Sabit ( Başvuru ve Onay sonrası )')) {
        if (isWritingProcess) {
          if (contractSigningDate) {
            const { start, end } = getDynamicDateRange(contractSigningDate, 2);
            if (start && end) {
              schedules = getMonthRange(start, end);
            }
          }
        } else if (isAfterApprovalProcess) {
          if (contractSigningDate && renewalPeriodMonths) {
            const { start, end } = getCurrentRenewalRange(
              contractSigningDate,
              renewalPeriodMonths,
              referenceDate
            );
            if (start && end) {
              schedules = getMonthRange(start, end);
            }
          }
        }
      } else if (pricingMethodName?.includes('Proje Yazım ( Sadece )')) {
        if (isWritingProcess) {
          if (contractSigningDate) {
            const { start, end } = getDynamicDateRange(contractSigningDate, 2);
            if (start && end) {
              schedules = getMonthRange(start, end);
            }
          }
        } else if (isAfterApprovalProcess) {
          warning = t('invoices.form.paymentSchedule.warnings.changePricingType');
        }
      } else if (pricingMethodName?.includes('Diğer - Tek Ödeme')) {
        if (contractSigningDate) {
          const { start, end } = getDynamicDateRange(contractSigningDate, 2);
          if (start && end) {
            schedules = getScheduleMonths(start, end);
          }
        }
      }
    }

    if (schedules.length === 0 && !warning) {
      if (isWritingProcess && contractSigningDate) {
        const { start, end } = getDynamicDateRange(contractSigningDate, 2);
        if (start && end) {
          schedules = getScheduleMonths(start, end);
        }
      } else if (isAfterApprovalProcess && workStartDate) {
        const months = renewalPeriodMonths || 12;
        const { start, end } = getDynamicDateRange(workStartDate, months);
        if (start && end) {
          schedules = getScheduleMonths(start, end);
        }
      } else if (contractSigningDate) {
        const { start, end } = getDynamicDateRange(contractSigningDate, 2);
        if (start && end) {
          schedules = getScheduleMonths(start, end);
        }
      } else {
        const today = new Date();
        const { start, end } = getDynamicDateRange(today, 2);
        if (start && end) {
          schedules = getScheduleMonths(start, end);
        }
      }
    }

    return { schedules, warning };
  };

  const { schedules, warning } = useMemo(() => {
    if (value?.length > 0) {
      // Eski format schedule'ları yeni formata çevir
      const updatedSchedules = value.map(schedule => {
        if (!schedule.yearMonth && schedule.monthName && schedule.year) {
          return {
            ...schedule,
            yearMonth: `${schedule.year} ${schedule.monthName}`
          };
        }
        return schedule;
      });
      return { schedules: updatedSchedules, warning: null };
    }
    
    if (customDateRange && dateRangeStart && dateRangeEnd) {
      const customSchedules = getCustomMonthRange();
      return { schedules: customSchedules, warning: null };
    }
    
    if (customDateRange && (!dateRangeStart || !dateRangeEnd)) {
      return { schedules: [], warning: null };
    }
    
    const { contractId, pricingProcessId } = formData;
    if (!contractId || !pricingProcessId) return { schedules: [], warning: null };
    
    return calculatePaymentSchedule();
  }, [value, formData.contractId, formData.pricingProcessId, formData.jobId, formData.firmId, formData.firm_short_name, formData.firm_established, customDateRange, dateRangeStart, dateRangeEnd]);

  useEffect(() => {
    if ((!value || value.length === 0) && schedules && schedules.length > 0) {
      onChange?.(field.name, schedules);
    }
  }, [schedules]);

  const [fillAmount, setFillAmount] = useState('');
  
  // Auto-populate fillAmount from parent amount or total unit price when available
  useEffect(() => {
    // Priority: parent amount (for split invoices) > total unit price (for normal invoices)
    if (parentAmount && parentAmount > 0) {
      setFillAmount(parentAmount.toString());
    } else if (totalUnitPrice && totalUnitPrice > 0 && !parentAmount) {
      setFillAmount(totalUnitPrice.toString());
    } else if ((parentAmount === null || parentAmount === undefined) && 
               (totalUnitPrice === null || totalUnitPrice === undefined)) {
      // Clear fillAmount if both are cleared
      setFillAmount('');
    }
  }, [parentAmount, totalUnitPrice]);
  
  const handleFillAll = () => {
    const amount = parseFloat(fillAmount);
    if (isNaN(amount) || amount <= 0) return;
    const newSchedules = (schedules || []).map(s => ({ ...s, amount }));
    onChange?.(field.name, newSchedules);
    if (onValidate) {
      setTimeout(() => onValidate('total_unit_price'), 100);
    }
    setFillAmount(''); // Clear the input after successful assignment
  };

  const handleAmountChange = (monthIndex, amount) => {
    const newSchedules = [...schedules];
    newSchedules[monthIndex].amount = parseFloat(amount) || 0;
    onChange?.(field.name, newSchedules);
    
    if (onValidate) {
      setTimeout(() => onValidate('total_unit_price'), 100);
    }
  };

  const handleTransferStart = (fromIndex) => {
    setTransferringFrom(fromIndex);
  };

  const handleTransferComplete = (toIndex) => {
    if (transferringFrom !== null && toIndex !== transferringFrom) {
      // Hedef ayın aktif olup olmadığını kontrol et
      if (!schedules[toIndex].is_active) {
        setTransferringFrom(null);
        return;
      }
      
      const newSchedules = [...schedules];
      const fromAmount = newSchedules[transferringFrom].amount;
      newSchedules[transferringFrom].is_transfer = true;
      newSchedules[transferringFrom].transferredTo = toIndex;
      newSchedules[transferringFrom].is_active = false; // Transfer edilen ayı pasif yap
      newSchedules[toIndex].amount = (newSchedules[toIndex].amount || 0) + fromAmount;
      
      onChange?.(field.name, newSchedules);
      
      if (onValidate) {
        setTimeout(() => onValidate('total_unit_price'), 100);
      }
    }
    
    setTransferringFrom(null);
  };

  const handleTransferCancel = () => {
    setTransferringFrom(null);
  };

  const handleTransferUndo = (fromIndex) => {
    const fromSchedule = schedules[fromIndex];
    if (!fromSchedule?.is_transfer) return;
    const toIndex = fromSchedule.transferredTo;
    if (toIndex === undefined || toIndex === null) return;

    const newSchedules = [...schedules];
    const fromAmount = newSchedules[fromIndex].amount || 0;
    const currentTargetAmount = parseFloat(newSchedules[toIndex].amount) || 0;
    const nextTargetAmount = currentTargetAmount - fromAmount;

    newSchedules[toIndex].amount = nextTargetAmount >= 0 ? nextTargetAmount : 0;
    newSchedules[fromIndex].is_transfer = false;
    newSchedules[fromIndex].transferredTo = undefined;
    newSchedules[fromIndex].is_active = true;

    onChange?.(field.name, newSchedules);

    if (onValidate) {
      setTimeout(() => onValidate('total_unit_price'), 100);
    }
  };


  const activeCount = schedules.length;
  const totalAmount = schedules.reduce((sum, s) => {
    if (s.is_transfer) {
      return sum; 
    }
    return sum + (parseFloat(s.amount) || 0);
  }, 0);

  const parentItem = field.parentItem || {};
  const generalAmount = parseFloat((parentItem?.amount) || formData?.total_unit_price) || 0;
  const mismatchedMonths = schedules
    .filter(s => !s.is_transfer && s.is_active)
    .filter(s => Math.abs((parseFloat(s.amount) || 0) - generalAmount) > 0.01)
    .map(s => ({
      label: s.yearMonth || `${s.year} ${s.monthName}`,
      amount: parseFloat(s.amount) || 0,
    }));

  const hasAmountMismatch = isEditMode && generalAmount > 0 && mismatchedMonths.length > 0;

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
        {label}
      </Typography>
      
      {warning && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
          <Typography variant="body2" color="warning.dark">
            {warning}
          </Typography>
        </Box>
      )}

      {hasAmountMismatch && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t('invoices.form.paymentSchedule.amountMismatchWarning')}
          </Typography>
          <Box sx={{ pl: 2 }}>
            {mismatchedMonths.map((m, i) => (
              <Typography key={i} variant="body2">
                - {m.label}: ₺{m.amount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} (≠ ₺{generalAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })})
              </Typography>
            ))}
          </Box>
        </Alert>
      )}

      {isEditMode && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            startIcon={<Edit />}
            onClick={() => setIsEditingAmounts(!isEditingAmounts)}
            color={isEditingAmounts ? "secondary" : "primary"}
          >
            {isEditingAmounts ? t('invoices.form.paymentSchedule.finishEditing') : t('invoices.form.paymentSchedule.updateAmounts')}
          </Button>
        </Box>
      )}
      { !isEditMode && (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'flex-start', 
        gap: 4, 
        mb: 2,
        p: 2,
        bgcolor: 'grey.50',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'grey.200'
      }}>
        <TextField
          size="small"
          label={t('invoices.form.paymentSchedule.fillAllLabel', 'Aylara Tutar Ata')}
          placeholder={t('invoices.form.paymentSchedule.fillAllPlaceholder', 'Tutar giriniz')}
          type="number"
          value={fillAmount}
          onChange={(e) => setFillAmount(e.target.value)}
          disabled={!!parentAmount || !!totalUnitPrice}
          helperText={
            parentAmount 
              ? t('invoices.form.paymentSchedule.autoFilledFromParent', 'Bu tutar fatura kodu tutarından otomatik alınmıştır')
              : totalUnitPrice 
                ? t('invoices.form.paymentSchedule.autoFilledFromTotal', 'Bu tutar toplam birim ücretinden otomatik alınmıştır')
                : ''
          }
          sx={{ 
            width: 250,
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
            }
          }}
          inputProps={{ min: 0, step: 0.01 }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleFillAll}
          disabled={!fillAmount || parseFloat(fillAmount) <= 0}
          startIcon={<ArrowForward />}
          sx={{ 
            minWidth: 180,
            textTransform: 'none',
            fontWeight: 600
          }}
        >
          {t('invoices.form.paymentSchedule.assignToMonths')}
        </Button>
      </Box>
      )}
      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead sx={paymentScheduleFieldStyles.tableHead}>
            <TableRow>
              <TableCell>Yıl/Ay</TableCell>
              <TableCell>{t('invoices.form.paymentSchedule.active')}</TableCell>
              <TableCell>{t('invoices.form.paymentSchedule.amount')}</TableCell>
              <TableCell>{t('invoices.form.paymentSchedule.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {schedules.map((schedule, index) => (
              <TableRow key={`${schedule.year}-${schedule.month}`}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {schedule.yearMonth}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      backgroundColor: schedule.is_active ? 'success.light' : 'grey.300',
                      color: schedule.is_active ? 'success.contrastText' : 'grey.600',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      border: `2px solid ${schedule.is_active ? 'success.main' : 'grey.400'}`,
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    {schedule.is_active ? '✓' : '✗'}
                  </Box>
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    type="number"
                    value={schedule.amount}
                    onChange={(e) => handleAmountChange(index, e.target.value)}
                    placeholder="0"
                    disabled={
                      (isEditMode && !isEditingAmounts) || // Disable in edit mode unless editing amounts
                      !schedule.is_active || 
                      transferringFrom === index || 
                      schedule.is_transfer
                    }
                    inputProps={{ min: 0, step: 0.01 }}
                    sx={
                      schedule.is_transfer ? 
                        paymentScheduleFieldStyles.amountInputTransferred :
                        (transferringFrom === index ? 
                          paymentScheduleFieldStyles.amountInputDisabled : 
                          paymentScheduleFieldStyles.amountInput
                        )
                    }
                  />
                  {schedule.is_transfer && (
                    <Typography variant="caption" sx={paymentScheduleFieldStyles.transferInfo}>
                      {t('invoices.form.paymentSchedule.transferredTo')} {schedules[schedule.transferredTo]?.monthName}
                      {schedules[schedule.transferredTo]?.year && 
                        ` (${schedules[schedule.transferredTo].year})`
                      }
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {transferringFrom === null ? (
                    <>
                      {schedule.is_transfer ? (
                        <Tooltip title={t('invoices.form.paymentSchedule.transferUndo') || 'Transferi Geri Al'}>
                          <IconButton
                            size="small"
                            onClick={() => handleTransferUndo(index)}
                            color="secondary"
                          >
                            <Undo />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        schedule.amount > 0 && schedule.is_active && (
                          <Tooltip title={t('invoices.form.paymentSchedule.transferStart')}>
                            <IconButton
                              size="small"
                              onClick={() => handleTransferStart(index)}
                              sx={paymentScheduleFieldStyles.startTransferButton}
                            >
                              <ArrowForward />
                            </IconButton>
                          </Tooltip>
                        )
                      )}
                    </>
                  ) : transferringFrom === index ? (
                    <Tooltip title={t('invoices.form.paymentSchedule.transferCancel')}>
                      <IconButton
                        size="small"
                        onClick={handleTransferCancel}
                        sx={paymentScheduleFieldStyles.cancelButton}
                      >
                        <ArrowBack />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    schedule.is_active && !schedule.is_transfer && (
                      <Tooltip title={t('invoices.form.paymentSchedule.transferTo')}>
                        <IconButton
                          size="small"
                          onClick={() => handleTransferComplete(index)}
                          sx={paymentScheduleFieldStyles.transferButton}
                        >
                          <ArrowBack />
                        </IconButton>
                      </Tooltip>
                    )
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

            <Typography variant="body2" sx={paymentScheduleFieldStyles.noteText} style={{ marginBottom: '10px' }}>
      {t('invoices.form.paymentSchedule.note')}
    </Typography>
      {transferringFrom !== null && (
        <Box sx={paymentScheduleFieldStyles.transferModeBox}>
          <Typography variant="body2" sx={paymentScheduleFieldStyles.transferModeText}>
            <strong>{t('invoices.form.paymentSchedule.transferMode')}:</strong> {schedules[transferringFrom]?.monthName} ayındaki 
            ₺{schedules[transferringFrom]?.amount?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} 
            {t('invoices.form.paymentSchedule.transferDescription')}
          </Typography>
        </Box>
      )}

      <Box sx={paymentScheduleFieldStyles.summaryChips}>
        <Chip
        onClick={() => {
        }}
          label={`${t('invoices.form.paymentSchedule.activePeriods')}: ${activeCount}`}
          variant="outlined"
          size="small"
          sx={paymentScheduleFieldStyles.activePeriodsChip}
        />
        <Chip
        onClick={() => {
        }}
          label={`${t('invoices.form.paymentSchedule.total')}: ₺${totalAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`}
          variant="outlined"
          size="small"
          sx={paymentScheduleFieldStyles.totalChip}
        />
      </Box>

      {error && (
        <Typography variant="caption" sx={paymentScheduleFieldStyles.errorText}>
          {helperText}
        </Typography>
      )}

    </Box>
  );
};

export default PaymentScheduleField;
